import { ModalV3, useNavigation, useTranslation } from '@helios/frontend-core';

import { PalmettoSlider, PalmettoTag } from '@palmetto/dsm-components';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import Form from '../../../../components/_forms/_uncontrolled/Form/Form';
import { ROUTE } from '../../../../routes';
import { formatNumber } from '../../../../utils/math';
import { ShopSearchParams } from '../../types';
import { FilterBottomSheetButtons, FilterModalButtons } from '../components/FilterModalButtons';
import { PRICE_INCREMENT, getInitialPriceRange } from './getSearchPriceRange';
import { useGlobalPriceRange } from './useGlobalPriceRange';

type PriceRangeFormData = {
  priceRange: [number, number];
};

const TRACKING_ID = 'price-filter';

interface PriceFilterModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  searchParams: ShopSearchParams;
}

export const PriceFilterModal = ({ isOpen, onDismiss, searchParams }: PriceFilterModalProps) => {
  const { openURL } = useNavigation();
  const { t } = useTranslation();
  const methods = useForm<PriceRangeFormData>();
  const formRef = useRef<HTMLFormElement>(null);

  const { min: globalMin, max: globalMax, isLoading: isLoadingProducts } = useGlobalPriceRange();

  const [initialMin, initialMax] = getInitialPriceRange(searchParams.msrp, globalMin, globalMax);

  useEffect(() => {
    methods.reset({ priceRange: [initialMin, initialMax] });
  }, [initialMin, initialMax]);

  const priceRange = methods.watch('priceRange') ?? [initialMin, initialMax];

  const handleApply: SubmitHandler<PriceRangeFormData> = ({ priceRange: [min, max] }) => {
    openURL({
      to: ROUTE.SHOP_SEARCH,
      search: { ...searchParams, msrp: { $gte: min, $lte: max } },
    });
    onDismiss();
  };

  const handleRemoveFilter = () => {
    openURL({ to: ROUTE.SHOP_SEARCH, search: { ...searchParams, msrp: undefined } });
    onDismiss();
  };

  const sharedButtonProps = {
    trackingIdPrefix: TRACKING_ID,
    onApply: () => formRef.current?.requestSubmit(),
    onRemove: handleRemoveFilter,
  };

  return (
    <ModalV3
      open={isOpen}
      onClose={onDismiss}
      trackingId={TRACKING_ID}
      bottomSheetProps={{
        bottomContent: <FilterBottomSheetButtons {...sharedButtonProps} onDismiss={onDismiss} />,
        showCloseButton: false,
      }}
      modalProps={{ bottomContent: <FilterModalButtons {...sharedButtonProps} /> }}
    >
      <h2 className="text-text-primary heading-400 mb-4">{t('shopSearch.productFilters.priceFilterModal.title')}</h2>
      <Form formRef={formRef} methods={methods} onSubmit={handleApply} trackingId={TRACKING_ID}>
        <PalmettoSlider
          key={`${globalMin}-${globalMax}`}
          size="sm"
          range
          showValue={false}
          step={PRICE_INCREMENT}
          min={globalMin}
          max={globalMax}
          defaultValue={isLoadingProducts ? [globalMin, globalMax] : [initialMin, initialMax]}
          disabled={isLoadingProducts}
          onValueChange={({ value }) => methods.setValue('priceRange', [value[0], value[1]])}
        />
        <div className="flex justify-between mt-8">
          <span className="flex flex-col items-center">
            <span className="text-text-primary">{t('shopSearch.productFilters.priceFilterModal.minLabel')}</span>
            <PalmettoTag className="ml-2" size="sm" icon="moneyDollarSign" label={formatNumber(priceRange[0])} />
          </span>
          <span className="flex flex-col items-center">
            <span className="text-text-primary">{t('shopSearch.productFilters.priceFilterModal.maxLabel')}</span>
            <PalmettoTag className="ml-2" size="sm" icon="moneyDollarSign" label={formatNumber(priceRange[1])} />
          </span>
        </div>
      </Form>
    </ModalV3>
  );
};
